<script>
  // Pricing → Credits (Miles 2026-11-05). The builder runs on the ONE BLV balance: credits (1 credit = 1¢ of AI at our
  // cost). No tiers, no buyer-type or offer limits. Credits are bought once on the BLV buy page (web only); the table
  // mirrors supabase/functions/capacity-checkout, the only place the price is decided.
  import { supabase } from '../lib/supabase.js';

  const POINTS = [[5, 2], [20, 11], [50, 32], [100, 70], [250, 187], [500, 400]];   // [you pay, $ of AI at cost]
  function credits(d) {
    for (let i = 0; i < POINTS.length - 1; i++) {
      const [d0, b0] = POINTS[i], [d1, b1] = POINTS[i + 1];
      if (d <= d1) return Math.floor((b0 + ((b1 - b0) * (d - d0)) / (d1 - d0)) * 100);
    }
    return 40000;
  }
  const off = (d) => Math.max(0, Math.round((credits(d) * (5 / credits(5)) - d) * 100) / 100);
  const money = (n) => (Number.isInteger(n) ? '$' + n.toLocaleString('en-US') : '$' + n.toFixed(2));
  const ROWS = [5, 20, 50, 100, 250, 500];
  const BUY = 'https://bestlifeventures.com/credits.html';

  let balance = $state(null);
  $effect(() => { supabase.rpc('fn_my_credits').then(({ data }) => { if (typeof data === 'number') balance = data; }); });
</script>

<div class="pricing">
  <div class="head">
    <h2>Credits</h2>
    <p class="muted">One balance for Elvie and everything on BLV. Buy once — no subscription. You also earn credits by doing things.</p>
  </div>

  <div class="usage">
    <div class="usage-label">Your balance</div>
    <div class="usage-row"><span>Credits</span><span class="usage-val">{balance == null ? '…' : balance.toLocaleString('en-US')}</span></div>
  </div>

  <div class="tiers">
    {#each ROWS as d (d)}
      <div class="tier {d === 100 ? 'pro' : ''}">
        <h3>{credits(d).toLocaleString('en-US')} credits</h3>
        <div class="price">{money(d)}</div>
        <ul><li>{off(d) >= 1 ? money(off(d)) + ' off' : 'The starting amount'}</li></ul>
        <button type="button" onclick={() => window.open(BUY, '_blank', 'noopener')}>Buy credits</button>
      </div>
    {/each}
  </div>
</div>

<style>
  .pricing {
    max-width: 980px;
    margin: 0 auto;
    padding: 48px 28px 64px;
    font-family: var(--font-sans);
    color: var(--text);
  }
  .head {
    text-align: center;
    margin-bottom: 36px;
  }
  h2 {
    font-family: var(--font-serif);
    font-size: 36px;
    font-weight: 400;
    letter-spacing: -0.6px;
    margin: 0 0 8px;
  }
  .muted {
    margin: 0;
    color: var(--text-muted);
    font-size: 14px;
    line-height: 1.5;
  }

  .usage {
    max-width: 480px;
    margin: 0 auto 36px;
    padding: 18px 22px;
    background: linear-gradient(180deg, rgba(20,18,22,0.85), rgba(12,11,14,0.85));
    border: 1px solid var(--border-strong);
    border-radius: 14px;
    backdrop-filter: blur(12px);
  }
  .usage-label {
    font-family: var(--font-mono);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 1.6px;
    color: var(--text-faint);
    margin-bottom: 12px;
    font-weight: 700;
  }
  .usage-row {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    padding: 6px 0;
    border-top: 1px solid var(--border);
  }
  .usage-row:first-of-type { border-top: none; }
  .usage-val {
    color: var(--text);
    font-family: var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.3px;
  }

  .tiers {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 18px;
    max-width: 960px;
    margin: 0 auto;
  }
  .tier {
    position: relative;
    background: linear-gradient(180deg, rgba(20,18,22,0.92) 0%, rgba(12,11,14,0.92) 100%);
    border: 1px solid var(--border-strong);
    border-radius: 18px;
    padding: 28px 24px 24px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    box-shadow:
      0 1px 0 rgba(255,255,255,0.06) inset,
      0 24px 50px rgba(0,0,0,0.45);
    backdrop-filter: blur(12px);
  }
  .tier.current {
    border-color: var(--gold);
    box-shadow:
      0 0 28px rgba(212,175,55,0.2),
      inset 0 0 0 1px rgba(212,175,55,0.1),
      0 24px 50px rgba(0,0,0,0.45);
  }
  .tier h3 {
    margin: 0;
    font-family: var(--font-mono);
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1.8px;
    color: var(--text-muted);
    font-weight: 700;
  }
  .price {
    font-family: var(--font-serif);
    font-size: 44px;
    font-weight: 400;
    color: var(--text);
    letter-spacing: -1px;
    line-height: 1;
    display: flex;
    align-items: baseline;
    gap: 8px;
  }
  .period {
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--text-muted);
    font-weight: 500;
    letter-spacing: 0.1px;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 4px 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  li {
    font-family: var(--font-sans);
    font-size: 13px;
    line-height: 1.5;
    color: var(--text);
    padding-left: 20px;
    position: relative;
  }
  li::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: var(--gold);
    font-size: 12px;
    font-weight: 700;
  }
  .tier button {
    margin-top: auto;
    background: linear-gradient(180deg, #F4D03F 0%, #D4AF37 55%, #A8861A 100%);
    color: #0a0a0c;
    border: 1px solid rgba(212,175,55,0.6);
    padding: 11px 18px;
    border-radius: 10px;
    cursor: pointer;
    font-family: var(--font-sans);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.2px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.5), 0 6px 18px rgba(212,175,55,0.25);
    transition: transform 0.15s ease;
  }
  .tier button:hover { transform: translateY(-1px); }

  .current-badge {
    margin-top: auto;
    text-align: center;
    font-family: var(--font-mono);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 1.6px;
    color: var(--gold);
    background: rgba(212,175,55,0.1);
    border: 1px solid var(--gold-line);
    padding: 9px 12px;
    border-radius: 999px;
    font-weight: 700;
  }

  .slot-section {
    max-width: 640px;
    margin: 36px auto 0;
    padding: 22px 24px;
    background: linear-gradient(180deg, rgba(20,18,22,0.85), rgba(12,11,14,0.85));
    border: 1px solid var(--border-strong);
    border-radius: 14px;
    text-align: center;
    backdrop-filter: blur(12px);
  }
  .slot-section h3 {
    margin: 0 0 6px;
    font-family: var(--font-serif);
    font-size: 18px;
    font-weight: 400;
    letter-spacing: -0.2px;
    color: var(--text);
  }
  .not-recommended {
    margin: 0 0 14px;
    font-family: var(--font-sans);
    font-size: 12px;
    color: var(--text-faint);
    font-style: italic;
    line-height: 1.5;
  }
  .slot-buttons {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
  }
  .slot-buttons button {
    background: transparent;
    color: var(--gold);
    border: 1px solid var(--gold-line);
    padding: 9px 16px;
    border-radius: 999px;
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.2px;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .slot-buttons button:hover { background: rgba(212,175,55,0.1); }

  .footnote {
    text-align: center;
    margin: 28px auto 0;
    font-family: var(--font-sans);
    font-size: 11px;
    color: var(--text-faint);
    max-width: 540px;
    line-height: 1.6;
  }
</style>
